"use client";

import React from "react";
import Cookies from "js-cookie";
import { useSession } from "next-auth/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { apiGet, apiPut } from "@/lib/api";
import { apiRoutes } from "@/lib/apiRoutes";
import { getBrowserBase } from "@/lib/apiBase";
import { readLocationCookie, writeLocationCookie } from "@/lib/location";
import { STATE_NAMES } from "@/lib/geo";

type MetroOpt = { code: number; label: string; slug: string };

function slugify(s: string): string {
  return String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function LocationModal({ open, onClose, onChanged }: { open: boolean; onClose: () => void; onChanged?: (state: string, metroSlug: string) => void }) {
  const { data: session, status } = useSession();
  const [states, setStates] = React.useState<{ code: string; name?: string }[]>([]);
  const [metros, setMetros] = React.useState<MetroOpt[]>([]);
  const [selState, setSelState] = React.useState<string>("");
  const [selMetro, setSelMetro] = React.useState<string>(""); // store slug; code looked up when saving
  const metrosCacheRef = React.useRef<Array<{ state: string; slug: string; label?: string }>>([]);
  const [setDefault, setSetDefault] = React.useState<boolean>(true);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  function asArray(input: any): any[] {
    try {
      if (Array.isArray(input)) return input;
      if (!input) return [];
      if (Array.isArray((input as any)?.items)) return (input as any).items;
      if (typeof input === 'object') return Object.values(input as any);
      return [];
    } catch { return []; }
  }

  // Load states on open (no fallbacks)
  React.useEffect(() => {
    if (!open) return;
    (async () => {
      setErr(null);
      try {
        const API = getBrowserBase();
        const res = await fetch(`${API}/api/filters/states`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`states ${res.status}`);
        const raw: any = await res.json();
        const statesArr: any[] = Array.isArray(raw)
          ? raw
          : (Array.isArray(raw?.states) ? raw.states : (Array.isArray(raw?.States) ? raw.States : asArray(raw?.states || raw?.States)));
        const metrosArr: any[] = Array.isArray(raw)
          ? []
          : (Array.isArray(raw?.metros) ? raw.metros : (Array.isArray(raw?.Metros) ? raw.Metros : asArray(raw?.metros || raw?.Metros)));
        metrosCacheRef.current = (metrosArr || []).map((m: any) => ({ state: String(m?.state || m?.State || '').toUpperCase(), slug: String(m?.slug || m?.Slug || '').toLowerCase(), label: String(m?.label || m?.Label || '') }));
        let loaded = (statesArr || []).map((o: any) => {
          const code = String(
            o?.code ?? o?.Code ?? o?.value ?? o?.abbr ?? o?.state ?? ''
          ).toUpperCase();
          const name = String(o?.name ?? o?.Name ?? o?.label ?? o?.Label ?? '');
          return { code, name };
        }).filter((x: any) => x.code && /^[A-Z]{2}$/.test(x.code));
        if (!loaded.length) {
          // Fallback to static US states if facets are empty/unavailable
          loaded = Object.entries(STATE_NAMES).map(([code, name]) => ({ code, name }));
        }
        // Dedup by code to avoid duplicate options
        const dedup = Array.from(new Map(loaded.map(s => [s.code, s])).values());
        setStates(dedup);
        // Prefill
        let stateCandidate = "";
        let metroCandidate = "";
        try {
          const loc = readLocationCookie();
          if (loc) { stateCandidate = loc.state || ""; metroCandidate = loc.metro || ""; }
        } catch {}
        // If state not in cookie but we have a metro, derive state from metros facet
        if (!stateCandidate && metroCandidate && metrosCacheRef.current.length) {
          const match = metrosCacheRef.current.find(m => m.slug === metroCandidate.toLowerCase());
          if (match?.state) stateCandidate = match.state;
        }
        setSelState(stateCandidate || (loaded[0]?.code || ""));
        if (stateCandidate) {
          // If we have a cookie metro, try to carry it forward
          setSelMetro(metroCandidate || "");
        } else {
          setSelMetro("");
        }
      } catch (e: any) {
        setErr(e?.message || 'Failed to load states');
        // Fallback: populate full US states so the DDL is usable even if facets fail
        const fallback = Object.entries(STATE_NAMES).map(([code, name]) => ({ code, name }));
        setStates(fallback);
      }
    })();
  }, [open]);

  // Load metros for state
  React.useEffect(() => {
    if (!open || !selState) { setMetros([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const API = getBrowserBase();
        const res = await fetch(`${API}/api/filters/metros?state=${encodeURIComponent(selState)}`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`metros ${res.status}`);
        const raw: any = await res.json();
        const metrosRaw: any[] = Array.isArray(raw) ? raw : asArray(raw);
        const list: MetroOpt[] = (metrosRaw || []).map((m: any) => {
          const label = String(m?.label || m?.Label || '');
          const value = String(m?.value || m?.Value || m?.code || m?.Code || '').toLowerCase();
          const code = typeof m?.code === 'number' ? m.code : (typeof m?.Code === 'number' ? m.Code : 0);
          const slug = value || slugify(label);
          return { code, label: label || slug, slug: slug.replace(/-market$/i, '') };
        }).filter((m: any) => m.slug);
        if (!cancelled) setMetros(list);
      } catch {}
    })();
    return () => { cancelled = true };
  }, [open, selState]);

  async function save() {
    if (!selState || !selMetro) return;
    setBusy(true);
    setErr(null);
    try {
      // Persist cookie(s) for both guests and authed users
      writeLocationCookie({ state: selState, metro: selMetro });
      // If authed + setDefault, write to profile (StateCode + MetroCode when available)
      if (status === 'authenticated' && setDefault) {
        try {
          const current = await apiGet<any>(apiRoutes.profile, session);
          const prof = (current?.profile ?? current?.Profile) || current || {};
          const match = metros.find(m => m.slug === selMetro);
          const metroCode = match?.code && match.code > 0 ? match.code : (prof?.metroCode ?? null);
          const toSave = {
            Profile: {
              ...(prof || {}),
              StateCode: selState,
              MetroCode: metroCode,
            }
          };
          await apiPut(apiRoutes.profile, session, toSave);
        } catch (e: any) {
          // Non-fatal; keep cookie success
          setErr(e?.message || 'Failed to save profile location');
        }
      }
      // Notify callers
      onChanged?.(selState, selMetro);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Choose Your Location</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {err && <div className="text-sm text-red-600">{err}</div>}
          <div>
            <Label htmlFor="state">State</Label>
            <select
              id="state"
              className="mt-1 h-10 w-full bg-background border rounded px-3"
              value={selState}
              onChange={(e) => { setSelState(e.target.value); setSelMetro(''); }}
            >
              <option value="">Select a state</option>
              {states.map((s, i) => (
                <option key={s.code || `state-${i}`} value={s.code}>{s.name || s.code}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="metro">Metro</Label>
            <select
              id="metro"
              className="mt-1 h-10 w-full bg-background border rounded px-3"
              value={selMetro}
              onChange={(e) => setSelMetro(e.target.value)}
              disabled={!selState || metros.length === 0}
            >
              <option value="">{!selState ? 'Select a state first' : (metros.length ? 'Select a metro' : 'No metros found')}</option>
              {metros.map((m, i) => (
                <option key={m.slug || `metro-${i}`} value={m.slug}>{m.label || m.slug}</option>
              ))}
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={setDefault} onChange={(e) => setSetDefault(e.target.checked)} />
            <span>Set as default</span>
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy || !selState || !selMetro}>{busy ? 'Saving.' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
